import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import type {CampaignApplicationOpenSerializer} from "@/generated/DjangoTypes";
import {queryClient} from "@/queryClient";
import useCampaignManagementStore from "@/store/campaignManagement";
import {create, type TSetter} from "@/store/createStore";
import {
  buildActiveFilters,
  captureSearchParamsDiffEvents,
  createDrawerActions,
  DEFAULT_FILTERS,
  DEFAULT_INITIAL_FILTERS,
  mergeFiltersPreservingCurrent,
  type TFilters as TInfluencerTFilters,
  type TInitialFilters,
} from "@/store/filterStoreUtils";
import type {TActiveFilter, TCampaignType, TNonFunctionKeys, TPaginated} from "@/store/types";
import {type InfiniteData} from "@tanstack/react-query";
import type {AxiosResponse} from "axios";
import {debounce, type DebouncedFuncLeading} from "lodash";
import qs from "qs";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TFilters = TInfluencerTFilters & {
  ordering: TInfluencerTFilters["ordering"] | "-recommendation_score";
};

type TState = {
  activeFilters: TActiveFilter[];
  defaultFilters: TFilters;
  debouncedPendingSearchParams: DebouncedFuncLeading<() => void>;
  debouncedSearchParams: DebouncedFuncLeading<() => void>;
  filters: TFilters;
  hasSubscription: boolean;
  initialFilters: TInitialFilters;
  isFilterDrawerOpen: boolean;
  isInitialFiltersInitialized: boolean;
  pendingSearchParams: string | null;
  savedFilters: TFilters | null;
  searchParams: string | null;
  applyDrawerFilters(): void;
  buildSearchParams(): string;
  cancelDrawerFilters(): void;
  getHistogram<
    K extends
      | "engagement__histogram"
      | "followers__histogram"
      | "median_views__histogram"
      | "tiktok_last_period_video_count__histogram",
  >(
    key: K,
  ): TState["initialFilters"][K];
  openDrawer(): void;
  reset(): void;
  resetAllFilters(): void;
  resetFilter(filterKeys: string[]): void;
  setFilters<K extends keyof TFilters, V extends TFilters[K]>(key: K, value: V, options?: {skipQuery?: boolean}): void;
  setInitialFilters(filters: TState["initialFilters"]): void;
  setSearchParams(searchParams: string, options?: {skipAnalytics?: boolean}): void;
  setSubscription(hasSubscription: boolean): void;
  setter: TSetter<TState>;
  setValue<K extends TNonFunctionKeys<TState>, V extends TState[K]>(key: K, value: V): void;
  updateActiveFilters(): void;
};

const initialState = {
  activeFilters: [],
  defaultFilters: DEFAULT_FILTERS,
  hasSubscription: false,
  initialFilters: DEFAULT_INITIAL_FILTERS,
  isFilterDrawerOpen: false,
  isInitialFiltersInitialized: false,
  filters: DEFAULT_FILTERS,
  pendingSearchParams: null,
  savedFilters: null,
  searchParams: null,
} as Pick<TState, TNonFunctionKeys<TState>>;

const getCampaignType = (): TCampaignType => {
  const campaign = useCampaignManagementStore.getState().campaign;
  return campaign.ugc ? "ugc" : campaign.tiktok ? "tiktok" : "instagram";
};

const useCampaignInfluencerFilterStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        debouncedPendingSearchParams: debounce(() => {
          const pendingParams = get().buildSearchParams();

          set(
            state => {
              state.pendingSearchParams = pendingParams;
            },
            false,
            "useCampaignInfluencerFilterStore/setPendingSearchParams",
          );
        }, 1000),
        debouncedSearchParams: debounce(() => {
          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
        }, 1000),
        buildSearchParams: () => {
          const filters = get().filters;

          const serializedParams = {
            ...filters,
            audience_country: filters.audience_country.join(","),
            audience_location_search: filters.audience_location_search.map(item => item.name).join(","),
            cutoff_value: filters.vibe ? filters.cutoff_value : undefined,
            influencer_country: filters.influencer_country.join(","),
            maximum_cutoff_value: filters.vibe ? filters.maximum_cutoff_value : undefined,
            feed_hashtags:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags.join(",")
                : undefined,
            feed_hashtags_mode:
              filters.is_competitor_filter_active && filters.feed_hashtags.length > 0
                ? filters.feed_hashtags_mode
                : undefined,
            feed_tags:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0
                ? filters.feed_tags.join(",")
                : undefined,
            feed_tags_mode:
              filters.is_competitor_filter_active && filters.feed_tags.length > 0 ? filters.feed_tags_mode : undefined,
            location_radius_km:
              filters.location_radius_km > 0 && filters.location_search.some(item => item.type !== "region")
                ? filters.location_radius_km
                : undefined,
            location_search: filters.location_search
              .filter(item => item.type !== "zip")
              .map(item => item.name)
              .join(","),
            location_zips: filters.location_search
              .filter(item => item.type === "zip")
              .map(item => `${item.country_code}:${item.name}`)
              .join(","),
          };

          return qs.stringify(serializedParams, {arrayFormat: "comma"});
        },
        setSearchParams: (searchParams, options) => {
          const previous = get().searchParams;
          set(
            state => {
              state.searchParams = searchParams;
            },
            false,
            "useCampaignInfluencerFilterStore/setSearchParams",
          );
          if (!options?.skipAnalytics) {
            captureSearchParamsDiffEvents(previous, searchParams, "application");
          }
        },
        setter: fn => set(fn, false, "useCampaignInfluencerFilterStore/setter"),
        setFilters: (key, value, options = {skipQuery: false}) => {
          get().debouncedSearchParams.cancel();

          set(
            state => {
              state.filters[key] = value;
            },
            false,
            "useCampaignInfluencerFilterStore/setFilters",
          );

          if (options.skipQuery) {
            return;
          }

          if (get().isFilterDrawerOpen) {
            get().debouncedPendingSearchParams();
            get().updateActiveFilters();

            return;
          }

          const searchParams = get().buildSearchParams();
          const cachedQuery = queryClient.getQueryData<
            InfiniteData<AxiosResponse<TPaginated<CampaignApplicationOpenSerializer>>>
          >(CAMPAIGN_KEYS.qCampaignOpenApplications(useCampaignManagementStore.getState().campaign.id, searchParams));

          if (cachedQuery?.pages) {
            get().debouncedSearchParams.cancel();
            get().setSearchParams(searchParams);
          } else {
            get().debouncedSearchParams();
          }

          get().updateActiveFilters();
        },
        setValue: (key, value) =>
          set(
            state => {
              state[key] = value;
            },
            false,
            "useCampaignInfluencerFilterStore/setValue",
          ),
        setSubscription: value => {
          set(
            state => {
              state.hasSubscription = value;
            },
            false,
            "useCampaignInfluencerFilterStore/setSubscription",
          );
          const params = get().buildSearchParams();
          get().setSearchParams(params);
        },
        setInitialFilters: filters => {
          const campaignType = getCampaignType();

          set(
            state => {
              state.initialFilters = {
                ...state.initialFilters,
                ...filters,
              };
              state.filters.followers_start = filters.followers__min;
              state.filters.followers_end = filters.followers__max;
              state.filters.engagement_start = filters.engagement__min;
              state.filters.engagement_end = filters.engagement__max;
              state.filters.median_views_start = filters.median_views__min;
              state.filters.median_views_end = filters.median_views__max;
              state.filters.tiktok_last_period_video_count_start = filters.tiktok_last_period_video_count__min;
              state.filters.tiktok_last_period_video_count_end = filters.tiktok_last_period_video_count__max;
              state.filters.rating_start = filters.rating__min;
              state.filters.rating_end = filters.rating__max;
              state.filters.type = campaignType;
              if (state.filters.ordering === null) {
                state.filters.ordering = "-recommendation_score" as TInfluencerTFilters["ordering"];
              }
              state.isInitialFiltersInitialized = true;
            },
            false,
            "useCampaignInfluencerFilterStore/setInitialFilters",
          );

          set(
            state => {
              state.defaultFilters = {...state.filters};
            },
            false,
            "useCampaignInfluencerFilterStore/setDefaultFilters",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams);
          get().updateActiveFilters();
        },
        ...createDrawerActions(get, set, "useCampaignInfluencerFilterStore"),
        resetAllFilters: () => {
          const current = get().filters;
          set(
            state => {
              state.filters = mergeFiltersPreservingCurrent(current, state.defaultFilters, {
                type: current.type,
              });
            },
            false,
            "useCampaignInfluencerFilterStore/resetAllFilters",
          );

          const searchParams = get().buildSearchParams();
          get().setSearchParams(searchParams, {skipAnalytics: true});
          get().updateActiveFilters();
        },
        resetFilter: filterKeys => {
          const defaults = get().defaultFilters;

          set(
            state => {
              state.filters = {
                ...state.filters,
                ...Object.fromEntries(filterKeys.map(key => [key, defaults[key as keyof TFilters]])),
              };
            },
            false,
            "useCampaignInfluencerFilterStore/resetFilter",
          );

          const pendingParams = get().buildSearchParams();

          set(
            state => {
              state.pendingSearchParams = pendingParams;
            },
            false,
            "useCampaignInfluencerFilterStore/resetFilter/setPendingSearchParams",
          );

          get().updateActiveFilters();
        },
        updateActiveFilters: () => {
          const filters = get().filters;
          const initialFilters = get().initialFilters;
          const active = buildActiveFilters(filters, initialFilters);

          set(
            state => {
              state.activeFilters = active;
            },
            false,
            "useCampaignInfluencerFilterStore/updateActiveFilters",
          );
        },
        getHistogram: key => get().initialFilters[key],
        reset: () => set(initialState, false, "useCampaignInfluencerFilterStore/reset"),
      }),
      {name: "useCampaignInfluencerFilterStore"},
    ),
  ),
);

export default useCampaignInfluencerFilterStore;
